import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft, ArrowRight, Compass, Home, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SiteNav } from "@/components/site/SiteNav";
import { SiteFooter } from "@/components/site/SiteFooter";
import { PageMeta } from "@/components/site/PageMeta";
import { PRIMARY_CTAS } from "@/content/primary-cta";
import { POPULAR_LINKS, suggestDestination } from "@/content/not-found-links";
import { trackCTA } from "@/lib/tracking";

/**
 * Custom 404. Instead of a dead end, it offers:
 *  1. the site-wide primary CTA (free scan),
 *  2. a best-guess "did you mean" link derived from the broken path,
 *  3. a grid of the highest-value pages,
 *  4. a way back / a way to reach a human.
 *
 * data-page="not-found" is the stable machine-readable marker the CI link
 * checker (scripts/link-check.ts) uses to detect soft-404s. A SPA always
 * answers 200 at the HTTP layer, so this attribute is the only reliable
 * signal that a route fell through to the catch-all. Do not remove.
 */
const NotFound = () => {
  const location = useLocation();
  const brokenPath = location.pathname;
  const suggestion = suggestDestination(brokenPath);
  const primary = PRIMARY_CTAS.home;

  useEffect(() => {
    console.error("404 Error: User attempted to access non-existent route:", brokenPath);
    trackCTA("not_found_view", {
      broken_path: brokenPath,
      referrer: typeof document !== "undefined" ? document.referrer : "",
      suggested: suggestion?.to ?? "none",
    });
  }, [brokenPath, suggestion?.to]);

  return (
    <div className="min-h-screen bg-background" data-page="not-found">
      <PageMeta
        title="Page Not Found | Dr. Sully Speaks"
        description="That page moved or never existed. Jump to the free Make Change Scan, the resources library, or any of the most-visited pages on DrSullySpeaks.com."
        path="/404"
        noindex
      />
      <SiteNav />

      <main className="mx-auto max-w-5xl px-4 pb-16 pt-28 md:pb-24 md:pt-36">
        <section className="text-center">
          <div className="mb-3 text-[10px] uppercase tracking-[0.3em] text-gold">Error 404</div>
          <h1 className="text-3xl font-bold md:text-5xl">This page moved — your next step didn't</h1>
          <p className="mx-auto mt-4 max-w-2xl text-sm text-muted-foreground md:text-base">
            The link you followed isn't a page on this site anymore. Nothing is lost — pick up
            right where you meant to start below.
          </p>
          {brokenPath !== "/" && (
            <p className="mt-3 break-all font-mono text-xs text-muted-foreground/70">
              Requested: {brokenPath}
            </p>
          )}

          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Button asChild size="lg">
              <Link
                to={primary.href}
                onClick={() =>
                  trackCTA("not_found_primary_cta", { broken_path: brokenPath, to: primary.href })
                }
              >
                {primary.label}
                <ArrowRight className="ml-2 h-4 w-4" aria-hidden />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <Link
                to="/ebooks"
                onClick={() =>
                  trackCTA("not_found_secondary_cta", { broken_path: brokenPath, to: "/ebooks" })
                }
              >
                Browse free resources
              </Link>
            </Button>
          </div>

          {suggestion && (
            <div className="mx-auto mt-8 max-w-lg rounded-xl border border-gold/40 bg-muted/40 px-5 py-4 text-sm">
              <span className="inline-flex items-center text-muted-foreground">
                <Compass className="mr-2 h-4 w-4 text-gold" aria-hidden />
                Were you looking for
              </span>{" "}
              <Link
                to={suggestion.to}
                className="font-semibold underline underline-offset-4 hover:text-gold"
                onClick={() =>
                  trackCTA("not_found_suggestion_click", {
                    broken_path: brokenPath,
                    to: suggestion.to,
                  })
                }
              >
                {suggestion.label}
              </Link>
              ?
            </div>
          )}
        </section>

        <section className="mt-16" aria-labelledby="popular-pages">
          <h2 id="popular-pages" className="text-center text-lg font-semibold md:text-xl">
            Most-visited pages
          </h2>
          <ul className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {POPULAR_LINKS.map((link) => (
              <li key={link.to}>
                <Link
                  to={link.to}
                  onClick={() =>
                    trackCTA("not_found_popular_click", {
                      broken_path: brokenPath,
                      to: link.to,
                      label: link.label,
                    })
                  }
                  className="group flex h-full flex-col rounded-xl border border-border bg-card p-5 transition-colors hover:border-gold"
                >
                  <span className="flex items-center justify-between font-semibold">
                    {link.label}
                    <ArrowRight
                      className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-1 group-hover:text-gold"
                      aria-hidden
                    />
                  </span>
                  <span className="mt-2 text-sm text-muted-foreground">{link.blurb}</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-14 flex flex-wrap items-center justify-center gap-4 text-sm">
          <button
            type="button"
            onClick={() => window.history.back()}
            className="inline-flex items-center text-muted-foreground underline underline-offset-4 hover:text-foreground"
          >
            <ArrowLeft className="mr-1 h-3 w-3" aria-hidden />
            Go back
          </button>
          <Link
            to="/"
            className="inline-flex items-center text-muted-foreground underline underline-offset-4 hover:text-foreground"
          >
            <Home className="mr-1 h-3 w-3" aria-hidden />
            Home
          </Link>
          <a
            href="mailto:DrSully@DrSullySpeaks.com"
            className="inline-flex items-center text-muted-foreground underline underline-offset-4 hover:text-foreground"
          >
            <Mail className="mr-1 h-3 w-3" aria-hidden />
            Report a broken link
          </a>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
};

export default NotFound;
